'use client';

import {
  LOGIN_METHODS,
  useAuthStore,
} from '@wwe-portal/web/providers/stores/auth-store';

import { ScopelyButton } from '@wwe-portal/ui/features/auth';
import { FC } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getScopelyId, LOGIN_OPERATION } from '@wwe-portal/web/libs/scopelyid';
import { useMutation } from '@tanstack/react-query';
import { toast } from '@wwe-portal/ui/components/toast';
import { useTranslations } from 'next-intl';
import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';

export const ScopelyLogin: FC<{ className?: string }> = ({ className }) => {
  const t = useTranslations();
  const {
    preLoginWithScopely,
    setAuthenticating,
    isAuthenticating,
    loginMethod,
  } = useAuthStore();
  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const state = uuidv4();
      preLoginWithScopely(state);
      // Holds the button in its loading state until the browser navigates away.
      // This used to be a bare `await wait(3000)` after `login()` — an arbitrary
      // timer that outlived the redirect on fast connections and was too short
      // on slow ones.
      setAuthenticating(true);
      const scopelyId = await getScopelyId();
      // `operation` is required by the 2.0 flow: it is what `getOperationFromState`
      // reads back on the callback to tell a login apart from an account link.
      await scopelyId.login({ state, operation: LOGIN_OPERATION });
    },
    onError: (error) => {
      // Reaching here means we never left the page — bootstrap could not resolve
      // the OAuth2 version, or `login()` failed to build the authorize URL.
      // Previously this rejected into an unhandled mutation and the button just
      // stopped spinning.
      setAuthenticating(false);
      addDDError(error, {
        reason: 'login failed',
        loginMethod: LOGIN_METHODS.WITH_SCOPELY,
      });
      toast({
        variant: 'error',
        content: t('something_went_wrong'),
      });
    },
  });
  const redirectToLogin = async () => {
    mutate();
  };

  const loading =
    (isAuthenticating && loginMethod === LOGIN_METHODS.WITH_SCOPELY) ||
    isPending;

  return (
    <ScopelyButton
      onClick={redirectToLogin}
      className={className}
      loading={loading}
      loadingText="Processing..."
    />
  );
};
